import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from './core/helpers/authentication.service';
import { Router, NavigationEnd, NavigationError } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { IdleCheckService } from './shared/services/idle-check.service';
import { ResponsiveFontService } from './shared/services/responsive-font.service';

export enum Operation {
  None,
  StoreCode,
  ShowLoginPage,
  ExchangeCode,
  Local,
  Register,
}

// ─── Constants ───────────────────────────────────────────────────────────────
const IDLE_TIMEOUT_MS = 25 * 60 * 1000; // 25 min — show inactive dialog
const LOGOUT_TIMEOUT_MS = 5 * 60 * 1000; //  5 min — auto logout after dialog

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'SmartComm';

  /**
   * Layout + dashboard skeleton shown from app start (token exchange, permissions loading)
   * until the first navigation has completed and the real layout can render.
   */
  showStartupSkeleton = true;

  /** The skeleton covers the whole startup/login flow (before and while the token is fetched) and logout. */
  get showSkeleton(): boolean {
    return this.showStartupSkeleton || this.authService.isLoggingOut();
  }

  public lastActiveDate$: Observable<Date>;

  private subscription = new Subscription();
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private logoutTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private inActiveDialog: any;

  // private worker = new Worker(new URL('./app.worker', import.meta.url), { type: 'module' });

  constructor(
    private dialog: MatDialog,
    private idleCheckService: IdleCheckService,
    private authService: AuthenticationService,
    private router: Router,
    private fontService: ResponsiveFontService
  ) {
    this.idleCheckService.setUp();
    this.lastActiveDate$ = this.idleCheckService.lastActive$;

    // worker.postMessage({ hello: 'world' });
    // worker.onmessage = (e) => console.log('From worker', e.data);
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.watchFirstNavigation();
    this.loadApp();
    this.idleStateStart();
    this.listenForContinueSession();
    this.fontService.setResponsiveFontSize();
  }

  /**
   * Hides the startup skeleton once the first navigation has finished (or failed).
   * A cancelled navigation (e.g. the guard waiting for the auth callback code to be exchanged)
   * keeps the skeleton visible; the redirect after login triggers the next navigation.
   */
  private watchFirstNavigation(): void {
    if (this.router.navigated) {
      this.showStartupSkeleton = false;
      return;
    }

    this.subscription.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd || event instanceof NavigationError),
          take(1)
        )
        .subscribe(() => (this.showStartupSkeleton = false))
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearTimers();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.fontService.setResponsiveFontSize();
  }

  // ─── Auth ──────────────────────────────────────────────────────────────────

  private loadApp(): void {
    if (this.authService.checkAuthentication()) return;

    const { href } = window.location;

    if (href.includes('register')) {
      this.getRegisterPage();
    } else if (href.includes('localhost')) {
      this.setLocalDevToken();
    } else {
      const { code, cleanUrl } = this.authService.consumeAuthCallbackUrl(href);

      if (code) {
        window.history.replaceState({}, document.title, cleanUrl);
        this.authService.getToken(code);
        return;
      }

      this.authService.setRedirectUrl(href);
      this.authService.login();
    }
  }

  private setLocalDevToken(): void {
    localStorage.clear();
    const token =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyU2Vzc2lvbkNvbnRleHQiOiJ7XCJVc2VySWRlbnRpdHlDb250ZXh0XCI6e1wiVXNlcklkXCI6MjM5LFwiRmlyc3ROYW1lXCI6XCJTdXBlckFkbWluXCIsXCJhY3RpdmVcIjp0cnVlLFwiTGFzdE5hbWVcIjpcIlVBVFwiLFwiTW9iaWxlTnVtYmVyXCI6XCI5ODIwNjU5NjE2XCIsXCJFbWFpbEFkZHJlc3NcIjpcInNtYXJ0Y29tbXVhdEBnbWFpbC5jb21cIixcIkRpc3BsYXlOYW1lXCI6XCJTdXBlckFkbWluVUFUXCIsXCJIYXNBZ3JlZW1lbnRTaWduZWRcIjp0cnVlLFwiSXNBY3RpdmVcIjp0cnVlLFwiQWN0aXZhdGlvbkxpbmtcIjpcIlwiLFwiUm9sZUlkXCI6MjYyLFwiRmVkZXJhdGVkSWRcIjpcImdiMDBjZDNlLTBhYzgtN2E5YS1iNjExLTdmNmY0NzNhZDM3MlwiLFwiQ29tcGFueUlkXCI6bnVsbCxcIkNvbXBhbnlOYW1lXCI6bnVsbCxcIlJvbGVOYW1lXCI6XCJFQkcuU3VwZXJBZG1pblwiLFwiVGltZVpvbmVJZFwiOlwiSW5kaWEgU3RhbmRhcmQgVGltZVwiLFwiSXNDdXN0QWRtaW5cIjpmYWxzZSxcIklzU3VwZXJBZG1pblwiOnRydWV9LFwiTGljZW5zZUNvbnRleHRcIjpudWxsfSIsIm5iZiI6MTc5MjU2MTIwMCwiZXhwIjoxNzkyNTYzMDAwLCJpc3MiOiJpT25lQ2xvdWQiLCJhdWQiOiJEMW9RbmxrUEZkZ1ZSSlpNNmF4THBLV3k2VDA0dTdMVyJ9.FdJDNyyfnVR4VrykcnIEMs7Fctf0wwrR7W2CWnIFwLI';
    // No reload needed: ngOnInit runs before the router's initial navigation and AppStartupService,
    // so the token is already available to the guard, interceptors and permission preload.
    this.authService.setTokenOnLocal(token);
  }

  private getRegisterPage(): void {
    const route = this.authService.checkAuthentication() ? '/dashboard' : '/register';
    this.router.navigate([route]);
  }

  // ─── Idle / Session ────────────────────────────────────────────────────────

  private idleStateStart(): void {
    const sub = this.lastActiveDate$.subscribe(() => {
      this.resetActivity();
      this.timeoutId = setTimeout(() => {
        sub.unsubscribe();
        this.openInactiveDialog();
        this.logoutTimeoutId = setTimeout(() => this.logoutUser(), LOGOUT_TIMEOUT_MS);
      }, IDLE_TIMEOUT_MS);
    });
    this.subscription.add(sub);
  }

  private resetActivity(): void {
    this.inActiveDialog?.close();
    this.clearTimers();
  }

  private openInactiveDialog(): void {
    localStorage.removeItem('continueSession');

    // Uncomment to enable dialog:
    // this.inActiveDialog = this.dialog.open(ConfirmationDialogComponent, {
    //   disableClose: true,
    //   autoFocus: true,
    //   width: '500px',
    //   data: {
    //     title: 'Attention!',
    //     message: 'Your session is about to expire in 5 Minutes. Click "Continue Session" to stay logged in',
    //     okButtonText: 'Continue Session',
    //     isCancel: false,
    //   },
    // });

    this.inActiveDialog?.afterClosed().subscribe(() => {
      this.setContinueSessionFlag();
      this.idleStateStart();
    });
  }

  private setContinueSessionFlag(): void {
    localStorage.setItem('continueSession', Date.now().toString());
  }

  private listenForContinueSession(): void {
    window.addEventListener('storage', ({ key, newValue }: StorageEvent) => {
      if (key === 'continueSession' && newValue) {
        this.inActiveDialog?.close();
      }
      if (key === this.authService.getAccessKey() && newValue) {
        window.location.reload();
      }
    });
  }

  private logoutUser(): void {
    this.authService.logout();
  }

  private clearTimers(): void {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.logoutTimeoutId) clearTimeout(this.logoutTimeoutId);
    this.timeoutId = null;
    this.logoutTimeoutId = null;
  }
}

// if (typeof Worker !== 'undefined') {
//   // Create a new
//   const worker = new Worker(new URL('./app.worker', import.meta.url));
//   worker.onmessage = ({ data }) => {
//     console.log(`page got message: ${data}`);
//   };
//   worker.postMessage('hello');
// } else {
//   // Web Workers are not supported in this environment.
//   // You should add a fallback so that your program still executes correctly.
// }
